/**
 * Live preview for the on-screen text builder: a low-res frame grabbed from the proxy at the line's
 * moment, with every styled row drawn over it in real time as the user types. Canvas mirrors the
 * renderer's rules (sizes as % of frame height, 5% side margins, band vs box) using each font's browser
 * stack; the burn at render time is the truth, this is the faithful sketch.
 */
import { useEffect, useRef } from 'react';
import { TEXT_FONTS, type TextLine, type VideoFormat } from '@studio/contracts';

const PREVIEW_WIDTH = 270;

/** The one implementation of the renderer's text rules on canvas, shared by sketch and thumbnail. */
export function drawTextLines(ctx: CanvasRenderingContext2D, lines: TextLine[], w: number, h: number): void {
  for (const line of lines) {
    if (!line.text.trim()) continue;
    const font = TEXT_FONTS.find((f) => f.id === line.font) ?? TEXT_FONTS[0];
    const size = (h * line.sizePct) / 100;
    const y = (h * line.yPct) / 100;
    ctx.font = `${line.italic ? 'italic ' : ''}${line.bold ? 'bold ' : ''}${size}px ${font.css}`;
    ctx.textAlign = line.align;
    ctx.textBaseline = 'middle';
    const x = line.align === 'left' ? w * 0.05 : line.align === 'right' ? w * 0.95 : w / 2;
    const alpha = Math.round(line.backgroundOpacity * 255).toString(16).padStart(2, '0');
    if (line.background === 'band') {
      ctx.fillStyle = `${line.backgroundColour}${alpha}`;
      ctx.fillRect(0, y - size * 0.85, w, size * 1.7);
    } else if (line.background === 'box') {
      const metrics = ctx.measureText(line.text);
      const pad = size * 0.3;
      const bx = line.align === 'left' ? x : line.align === 'right' ? x - metrics.width : x - metrics.width / 2;
      ctx.fillStyle = `${line.backgroundColour}${alpha}`;
      ctx.fillRect(bx - pad, y - size * 0.6 - pad / 2, metrics.width + pad * 2, size * 1.2 + pad);
    }
    if (line.shadow) {
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowOffsetX = size * 0.05;
      ctx.shadowOffsetY = size * 0.05;
      ctx.shadowBlur = size * 0.05;
    }
    const outlineW = line.outlineW !== null
      ? size * 0.03 * line.outlineW
      : line.outline === 'thick' ? size * 0.08 : line.outline === 'thin' ? size * 0.04 : 0;
    if (outlineW > 0) {
      ctx.lineWidth = Math.max(1, outlineW) * 2;
      ctx.strokeStyle = line.outlineColour;
      ctx.lineJoin = 'round';
      ctx.strokeText(line.text, x, y);
    }
    ctx.fillStyle = line.colour;
    ctx.fillText(line.text, x, y);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }
}

/** A low-res draft of the burned text laid over a slot thumbnail; the click-through stays free. */
export function TextOverlay({ lines }: { lines: TextLine[] }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const paint = () => {
      const ctx = canvas.getContext('2d');
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!ctx || !w || !h) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.scale(dpr, dpr);
      drawTextLines(ctx, lines, w, h);
    };
    paint();
    const ro = new ResizeObserver(paint);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [lines]);
  return <canvas ref={ref} className="text-overlay" aria-hidden="true" />;
}

export function TextPreview({ lines, format, proxyUrl, frameMs }: {
  lines: TextLine[];
  format: VideoFormat;
  proxyUrl: string | null;
  frameMs: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const frameReadyRef = useRef(false);

  const ratio = format === '9:16' ? 16 / 9 : format === '1:1' ? 1 : 9 / 16;
  const w = PREVIEW_WIDTH;
  const h = Math.round(PREVIEW_WIDTH * ratio);

  const draw = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    const v = videoRef.current;
    if (v && frameReadyRef.current && v.videoWidth) {
      // Cover-fit the frame, like the renderer's scale and crop.
      const scale = Math.max(w / v.videoWidth, h / v.videoHeight);
      const dw = v.videoWidth * scale;
      const dh = v.videoHeight * scale;
      ctx.drawImage(v, (w - dw) / 2, (h - dh) / 2, dw, dh);
    }
    drawTextLines(ctx, lines, w, h);
  };

  // The frame: a hidden video seeks to the moment; a tainted canvas still displays, so no CORS needed.
  useEffect(() => {
    frameReadyRef.current = false;
    if (!proxyUrl) {
      draw();
      return;
    }
    const v = videoRef.current ?? document.createElement('video');
    videoRef.current = v;
    v.muted = true;
    v.preload = 'auto';
    const onSeeked = () => {
      frameReadyRef.current = true;
      draw();
    };
    v.addEventListener('seeked', onSeeked);
    const seek = () => {
      v.currentTime = frameMs / 1000;
    };
    if (v.src !== proxyUrl) {
      v.src = proxyUrl;
      v.addEventListener('loadedmetadata', seek, { once: true });
    } else seek();
    return () => v.removeEventListener('seeked', onSeeked);
  }, [proxyUrl, frameMs]); // draw is stable per render; frame state lives in refs

  useEffect(draw, [lines, format]); // redraw on styling changes; the frame ref persists

  return <canvas ref={canvasRef} className="text-preview" role="img" aria-label="On-screen text preview" />;
}
